// Recuperação automática de Service Worker corrompido.
//
// Problema: Service Workers podem ficar "stuck" em versões antigas com bugs.
// Quando o SW velho lança exception no handler de fetch, o próprio mecanismo
// de update do SW pode parar de funcionar (ele depende de fetch do sw.js
// pra detectar nova versão). Loop infinito: SW quebrado bloqueia atualização.
//
// Caso real: v1.4.0 deployou sw.ts com `event: undefined as never` que
// crasha CacheFirst.handle em Workbox 7+. Browsers que pegaram esse SW
// ficaram travados — todo request de imagem retornava `no-response` no
// console. Fix da v1.4.1 nunca chegava porque o SW velho cascateava
// erros antes do mecanismo de update rodar.
//
// Solução (defesa em camadas):
//
// === Camada 1: AUTO-RECOVERY (este módulo) ===
// Listener global de `unhandledrejection` + `error`. Conta erros tipo SW
// nos últimos 30s. Se passar de THRESHOLD, executa recuperação:
//   1. Unregister de todos SWs registrados
//   2. Limpa todos os caches do Cache API
//   3. Marca flag em sessionStorage pra evitar loop de reload
//   4. Reload da página
// User vê 1 piscar de tela e tudo funciona.
//
// === Camada 2: BOTÃO MANUAL (SettingsScreen) ===
// Mesma função exportada (`runRecovery`) é chamada pelo botão "Limpar
// cache e dados do app" no Settings → seção Data. Pra quando user
// nota lentidão/bug sem trigger automático.
//
// === Camada 3: PÁGINA /recover (servida pelo backend) ===
// HTML standalone fora do escopo do SW. Pra catástrofes onde nem o
// shell JS carrega.
//
// === Camada 4: VERSIONED SW URL (vite-plugin-pwa) ===
// Build hash no path do sw.js pra forçar fetch novo a cada deploy.

const ERROR_WINDOW_MS = 30_000
const ERROR_THRESHOLD = 3
const RECOVERY_FLAG_KEY = 'iptv:sw-recovery-in-progress'
const RECOVERY_TIMESTAMP_KEY = 'iptv:sw-last-recovery-ts'
const MIN_RECOVERY_INTERVAL_MS = 60_000 // 1 min entre recoveries (anti-loop)

const errorTimestamps: number[] = []
let recoveryInProgress = false

/**
 * Detecta se uma mensagem de erro indica problema do SW.
 * Padrões conhecidos:
 *   - "Cannot read properties of undefined (reading 'waitUntil')" (v1.4.0 bug)
 *   - "no-response: no-response :: " (Workbox CacheFirst sem response)
 *   - "Failed to fetch" em URLs do nosso proxy (502 cascateando)
 *   - "The FetchEvent for ... resulted in a network error response"
 */
export function isSwError(message: string): boolean {
  if (!message) return false
  return (
    message.includes("reading 'waitUntil'") ||
    message.includes('no-response: no-response') ||
    message.includes('The FetchEvent for') ||
    /CacheFirst\.handle|Strategy\.handle.*StrategyHandler/.test(message)
  )
}

/**
 * Detecta falha de carregamento de chunk hasheado (lazy import 404 pós-deploy).
 *
 * Sintoma do cache stale: um index velho (servido antes do fix A, ou ainda
 * cacheado num device preso num SW velho) referencia um chunk `assets/x-HASH.js`
 * que sumiu do nginx no novo deploy. O dynamic import rejeita com uma destas
 * mensagens (variam por browser/bundler):
 *   - "ChunkLoadError" / "Loading chunk N failed"
 *   - "Failed to fetch dynamically imported module"
 *   - "error loading dynamically imported module"
 *   - "Importing a module script failed" (Safari/WebKit)
 *   - "'text/html' is not a valid JavaScript MIME type" (nginx serve o index
 *     no lugar do .js que sumiu → MIME errado)
 *
 * Distinto de isSwError: chunk error é sinal INEQUÍVOCO de index velho, então
 * dispara recovery na PRIMEIRA ocorrência (vs threshold 3 do SW error), porém
 * SEMPRE respeitando o guard anti-loop isRecentlyRecovered().
 */
export function isChunkError(message: string): boolean {
  if (!message) return false
  return (
    message.includes('ChunkLoadError') ||
    /Loading chunk \S+ failed/.test(message) ||
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('error loading dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('is not a valid JavaScript MIME type')
  )
}

/**
 * True se a mensagem justifica recovery automático (SW corrompido OU chunk
 * velho). Usado pelo Error Boundary global (RecoveryBoundary) pra decidir se
 * dispara recovery silencioso ou mostra fallback genérico. Reusa o mesmo guard
 * anti-loop via runRecovery (isRecentlyRecovered).
 */
export function isRecoverableError(message: string): boolean {
  return isChunkError(message) || isSwError(message)
}

/**
 * Executa o recovery: unregister + clear caches + reload.
 * Exportada pra ser chamada também pelo botão no Settings.
 *
 * @param reason  motivo (logado pra telemetria)
 * @param skipReload  se true, faz só unregister/clear (chamado da UI quando user prefere navegar manual)
 */
export async function runRecovery(reason: string, skipReload = false): Promise<void> {
  if (recoveryInProgress) return
  recoveryInProgress = true
  console.warn(`[sw-recovery] iniciando recovery: ${reason}`)

  try {
    // 1. Unregister todos SWs
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)))
      console.warn(`[sw-recovery] unregistered ${regs.length} SW(s)`)
    }

    // 2. Limpa Cache API (storage do SW)
    if (typeof caches !== 'undefined' && caches.keys) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)))
      console.warn(`[sw-recovery] deletados ${keys.length} caches`)
    }

    if (skipReload) {
      // skipReload=true: chamado por UI que prefere navegar manual. Sem reload,
      // precisamos liberar a flag pra próximas tentativas.
      recoveryInProgress = false
      return
    }

    // 3. Flag anti-loop: se reload disparar mesmo erro de novo,
    // refazer recovery viraria reload-loop. Flag + timestamp impedem.
    // localStorage no timestamp = cross-tab safe (sessionStorage é per-tab,
    // 2 tabs abertas poderiam fazer 2 recoveries em paralelo).
    try {
      sessionStorage.setItem(RECOVERY_FLAG_KEY, '1')
      localStorage.setItem(RECOVERY_TIMESTAMP_KEY, String(Date.now()))
    } catch {
      // storage pode estar bloqueado (private mode / quota) — segue mesmo assim
    }

    // 4. Reload — força bypass cache (true em FF, ignorado em Chrome mas reload sempre)
    console.warn('[sw-recovery] recarregando página')
    window.location.reload()

    // Cinto-suspensório: se reload falhar silenciosamente (já vi Tizen 2017
    // travar reload dentro de SW lifecycle), libera flag em 5s pra que
    // segunda tentativa do usuário não fique presa em no-op silencioso.
    setTimeout(() => {
      recoveryInProgress = false
    }, 5000)
  } catch (err) {
    console.error('[sw-recovery] falhou:', err)
    recoveryInProgress = false
  }
}

/**
 * Recovery COM guard anti-loop embutido. Pro Error Boundary global
 * (RecoveryBoundary), que captura o erro UMA vez no render e precisa do mesmo
 * tratamento "1 reload por MIN_RECOVERY_INTERVAL_MS" que o recordError dos
 * listeners já tem. Se um recovery aconteceu há pouco, NÃO reloada de novo
 * (loga) e devolve false → o boundary mostra o fallback em vez de loopar.
 *
 * @returns true se disparou o recovery; false se foi suprimido pelo anti-loop.
 */
export function runRecoveryIfNotRecent(reason: string): boolean {
  if (isRecentlyRecovered()) {
    console.warn(`[sw-recovery] ${reason} — recovery recente, suprimindo (anti-loop)`)
    return false
  }
  void runRecovery(reason)
  return true
}

/**
 * Verifica se acabamos de fazer um recovery (evita loop).
 * Retorna true se recovery aconteceu nos últimos MIN_RECOVERY_INTERVAL_MS.
 */
function isRecentlyRecovered(): boolean {
  try {
    // localStorage = cross-tab. Antes era sessionStorage (per-tab) e 2 tabs
    // simultâneas poderiam fazer 2 recoveries.
    const ts = Number(localStorage.getItem(RECOVERY_TIMESTAMP_KEY) ?? '0')
    if (!ts || Number.isNaN(ts)) return false
    return Date.now() - ts < MIN_RECOVERY_INTERVAL_MS
  } catch {
    return false
  }
}

/**
 * Inicializa os listeners globais de detecção.
 * Idempotente — pode ser chamado em múltiplos lugares sem efeito duplicado.
 */
let initialized = false
export function initSwAutoRecovery(): void {
  if (initialized) return
  initialized = true
  if (typeof window === 'undefined') return

  function recordError(msg: string): void {
    const chunkErr = isChunkError(msg)
    if (!isSwError(msg) && !chunkErr) return

    // Se acabamos de recuperar, IGNORAR — provavelmente o erro persiste
    // por outra causa (rede caída, etc) e fazer recovery de novo só
    // recarrega infinitamente. Loga mas não age. Vale tanto pra SW error
    // quanto pra chunk error: 1 reload por MIN_RECOVERY_INTERVAL_MS, no máximo.
    if (isRecentlyRecovered()) {
      console.warn(`[sw-recovery] erro pós-recovery (${msg.slice(0, 80)}) — ignorando pra evitar loop`)
      return
    }

    // Chunk error é sinal INEQUÍVOCO de index velho apontando pra chunk que
    // sumiu (cache stale pós-deploy). Recovery na 1ª ocorrência, sem esperar
    // o threshold do SW error — mas o guard isRecentlyRecovered() acima já
    // garante que nunca vira loop (após o reload, o Fix A traz index fresh do
    // nginx → chunks válidos → sem novo erro). recordError() é chamado só após
    // o guard, então 1 chunk error = 1 reload.
    if (chunkErr) {
      void runRecovery(`chunk error (index velho pos-deploy): ${msg.slice(0, 80)}`)
      return
    }

    const now = Date.now()
    // Limpa erros fora da janela
    while (errorTimestamps.length > 0 && now - errorTimestamps[0]! > ERROR_WINDOW_MS) {
      errorTimestamps.shift()
    }
    errorTimestamps.push(now)

    if (errorTimestamps.length >= ERROR_THRESHOLD) {
      void runRecovery(`${errorTimestamps.length} erros de SW em ${ERROR_WINDOW_MS / 1000}s`)
    }
  }

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason
    const msg = reason instanceof Error ? reason.message : String(reason ?? '')
    recordError(msg)
  })

  window.addEventListener('error', (e: ErrorEvent) => {
    recordError(e.message ?? '')
  })
}

/** Pra testes: reseta state interno e expõe helpers. NÃO usar em produção. */
export function __resetForTests(): {
  recordError: (msg: string) => void
  isSwError: (msg: string) => boolean
  isChunkError: (msg: string) => boolean
  isRecentlyRecovered: () => boolean
} {
  if (process.env['NODE_ENV'] === 'production') {
    throw new Error('__resetForTests() is forbidden in production')
  }
  initialized = false
  recoveryInProgress = false
  errorTimestamps.length = 0
  // recordError não é exportado normalmente — wrap aqui pra testes verem
  // o efeito dos listeners (que é o que queremos exercitar). Espelha a lógica
  // real: chunk error NÃO entra no errorTimestamps (no real ele faz recovery
  // direto e dá return antes de empilhar); aqui só não conta (não dispara
  // recovery em testes — verificamos contagem e detecção).
  return {
    recordError: (msg: string) => {
      if (!isSwError(msg) && !isChunkError(msg)) return
      if (isRecentlyRecovered()) return
      if (isChunkError(msg)) return // chunk error → recovery direto (não conta)
      const now = Date.now()
      while (errorTimestamps.length > 0 && now - errorTimestamps[0]! > ERROR_WINDOW_MS) {
        errorTimestamps.shift()
      }
      errorTimestamps.push(now)
    },
    isSwError,
    isChunkError,
    isRecentlyRecovered,
  }
}

/** Pra testes: lê estado interno sem efeito colateral. */
export function __getStateForTests(): { errorCount: number; initialized: boolean } {
  return { errorCount: errorTimestamps.length, initialized }
}
